/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injector } from '@angular/core';
import { Commands } from 'bridge-commands/commands';
import { BridgeService } from '../bridge/bridge.service';

/**
 * Batched LoggingService -> native client log forwarding.
 *
 * Platform-neutral: batches are sent as the `Logging.ForwardLogs` bridge command,
 * so any native client that implements it picks the logs up without the bridge
 * layer knowing that log forwarding exists.
 *
 * Forwarding stays off until a product enables it (see `LoggingService.initialize`),
 * so platforms whose native side does not implement the command never emit it.
 *
 * `BridgeService` is resolved through the injector on flush rather than injected:
 * it is built with a `Logger`, so requesting it any earlier would close a DI cycle.
 */

const NATIVE_LOG_FLUSH_MS = 100;

export interface NativeLogEntry {
    level: string;
    tag: string;
    text: string;
}

let bridgeInjector: Injector | null = null;
let queue: NativeLogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let dispatching = false;

function disableNativeLogForwarding(): void {
    bridgeInjector = null;
    queue = [];
    if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
}

function postNativeLogBatch(entries: NativeLogEntry[]): void {
    if (!bridgeInjector || entries.length === 0) {
        return;
    }
    // Anything logged while the batch is on its way out is dropped rather than
    // queued, otherwise a failing transport would keep feeding its own errors back.
    dispatching = true;
    try {
        bridgeInjector
            .get(BridgeService)
            .executeCommand(Commands.Common.Logging.ForwardLogs, { entries }, false, false)
            .subscribe({
                // A client that does not implement the command logs the rejection,
                // which would feed the next batch, so stop forwarding on first failure.
                error: () => disableNativeLogForwarding()
            });
    } catch {
        // no-op: bridge unavailable
    } finally {
        dispatching = false;
    }
}

function flushNativeLogQueue(): void {
    flushTimer = null;
    if (queue.length === 0) {
        return;
    }
    const batch = queue;
    queue = [];
    postNativeLogBatch(batch);
}

function scheduleNativeLogFlush(): void {
    if (flushTimer !== null) {
        return;
    }
    flushTimer = setTimeout(flushNativeLogQueue, NATIVE_LOG_FLUSH_MS);
}

if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => flushNativeLogQueue());
}

/**
 * Enables forwarding and supplies the injector used to reach the bridge. The
 * injector is only held while forwarding is on, so nothing resolves the bridge
 * on platforms that leave the feature off.
 */
export function setNativeLogForwardingEnabled(enabled: boolean, injector: Injector): void {
    if (enabled) {
        bridgeInjector = injector;
    } else {
        disableNativeLogForwarding();
    }
}

export function forwardLogToNative(level: string, tag: string, text: string): void {
    if (!bridgeInjector || dispatching || !text) {
        return;
    }
    queue.push({ level, tag, text });
    scheduleNativeLogFlush();
}
